import type { BuilderLegalStructureSourceDocument } from "@/lib/api/databook";

import type {
  FileCadence,
  FileCoverageStatus,
  FileCoverageYear,
} from "./LegalEntityFileCoverage";
import { normalizeCoveragePeriods } from "./coveragePeriodSemantics";

export interface FileCoveragePeriod {
  value: string;
  status: Extract<FileCoverageStatus, "received" | "missing">;
}

export function coverageYearLabel(value: string): string {
  return /^\d{4}$/.test(value) ? `FY${value.slice(-2)}` : value;
}

export function monthlyCoverageRows(
  periods: FileCoveragePeriod[],
  financialYearEndMonth: number,
): FileCoverageYear[] {
  if (periods.length === 0) return [];
  const statuses = new Map(periods.map((period) => [period.value, period.status]));
  const indexes = periods.map((period) => {
    const [year, month] = period.value.split("-").map(Number);
    return year * 12 + month - 1;
  });
  const first = Math.min(...indexes);
  const last = Math.max(...indexes);
  const fiscalYear = (index: number) => {
    const year = Math.floor(index / 12);
    const month = index % 12 + 1;
    return month <= financialYearEndMonth ? year : year + 1;
  };
  const firstFiscalYear = fiscalYear(first);
  const lastFiscalYear = fiscalYear(last);

  return Array.from(
    { length: lastFiscalYear - firstFiscalYear + 1 },
    (_, offset) => {
      const year = firstFiscalYear + offset;
      return {
        label: coverageYearLabel(String(year)),
        cells: Array.from<FileCoverageStatus>(
          { length: 12 },
          (_, monthIndex) => {
            const month = monthIndex + 1;
            const calendarYear = month <= financialYearEndMonth
              ? year
              : year - 1;
            const index = calendarYear * 12 + monthIndex;
            if (index < first || index > last) return "outside";
            const value = `${calendarYear}-${String(month).padStart(2, "0")}`;
            return statuses.get(value) ?? "missing";
          },
        ),
      };
    },
  );
}

export interface SourceCategory {
  id: string;
  label: string;
  kind: "table" | "document";
  type: string;
  cadence: FileCadence;
  sources: BuilderLegalStructureSourceDocument[];
  months: FileCoveragePeriod[];
  years: FileCoveragePeriod[];
}

export const TABLE_TYPE_LABELS: Record<string, string> = {
  pnl: "P&L",
  balance_sheet: "Balance sheet",
  trial_balance: "Trial balance",
  management_accounts: "Management accounts",
  cash_flow: "Cash flow",
  ar_aging: "Accounts receivable ageing",
  ar_by_debtor: "Accounts receivable by debtor",
  ap_aging: "Accounts payable ageing",
  ap_by_creditor: "Accounts payable by creditor",
  inventory_aging: "Inventory ageing",
  revenue: "Revenue",
  purchases_by_supplier: "Purchases by supplier",
  fx_rates: "FX rates",
  inventory_by_type: "Inventory by type",
  wip_by_type: "WIP by type",
  prepayments: "Prepayments",
  sundry_debtors: "Sundry debtors",
  sundry_creditors: "Sundry creditors",
  fixed_assets_register: "Fixed assets register",
  intangibles: "Intangibles",
  capex: "Capex",
  capex_commitments: "Capex commitments",
  bank_statements: "Bank statements",
  fte: "FTE and payroll",
  accrued_income: "Accrued income",
  accrued_expenses: "Accrued expenses",
  provisions: "Provisions",
  normalisation: "Normalisation",
  eliminations: "Eliminations",
  reconciliation: "Reconciliation",
  kpi: "KPI",
  other: "Other financial table",
};

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  information_memorandum: "Information memorandum",
  accounting_policies: "Accounting policies",
  statutory_accounts: "Statutory accounts",
  other: "Other document",
};

const QUARTERLY_TABLE_TYPES = new Set([
  "inventory_aging",
  "inventory_by_type",
  "wip_by_type",
]);

const ANNUAL_DOCUMENT_TYPES = new Set([
  "accounting_policies",
  "statutory_accounts",
]);

function readableSourceType(type: string): string {
  const words = type.split("_").filter(Boolean);
  if (words.length === 0) return "Unclassified";
  return words
    .map((word, index) => (
      index === 0
        ? `${word.charAt(0).toUpperCase()}${word.slice(1)}`
        : word
    ))
    .join(" ");
}

function sourceTypeCadence(
  kind: "table" | "document",
  type: string,
): FileCadence {
  if (kind === "table") {
    return QUARTERLY_TABLE_TYPES.has(type) ? "quarterly" : "monthly";
  }
  return ANNUAL_DOCUMENT_TYPES.has(type) ? "annual" : "adhoc";
}

function monthIndex(value: string): number {
  const [year, month] = value.split("-").map(Number);
  return year * 12 + month - 1;
}

function completeMonths(values: Set<string>): FileCoveragePeriod[] {
  if (values.size === 0) return [];
  const observed = Array.from(values).sort();
  const start = monthIndex(observed[0]);
  const end = monthIndex(observed[observed.length - 1]);
  return Array.from({ length: end - start + 1 }, (_, offset) => {
    const index = start + offset;
    const value = `${Math.floor(index / 12).toString().padStart(4, "0")}-${((index % 12) + 1).toString().padStart(2, "0")}`;
    return { value, status: values.has(value) ? "received" : "missing" };
  });
}

function completeYears(values: Set<string>): FileCoveragePeriod[] {
  if (values.size === 0) return [];
  const years = Array.from(values, Number);
  const start = Math.min(...years);
  const end = Math.max(...years);
  return Array.from({ length: end - start + 1 }, (_, offset) => {
    const value = String(start + offset);
    return { value, status: values.has(value) ? "received" : "missing" };
  });
}

function categoryPeriods(
  category: SourceCategory,
  financialYearEndMonth: number,
): Pick<SourceCategory, "months" | "years"> {
  if (category.kind !== "table") return { months: [], years: [] };
  const coverage = category.sources.flatMap((source) =>
    source.period_coverage.filter(
      (entry) => entry.table_type === category.type,
    ),
  );
  const months = new Set(
    coverage.flatMap((entry) =>
      entry.months
        .filter((period) => period.status === "received")
        .map((period) => period.value),
    ),
  );
  const years = new Set(
    coverage.flatMap((entry) =>
      entry.years
        .filter((period) => period.status === "received")
        .map((period) => period.value),
    ),
  );
  const normalized = normalizeCoveragePeriods(
    category.type,
    Array.from(months, (value) => ({ value, status: "received" })),
    Array.from(years, (value) => ({ value, status: "received" })),
    financialYearEndMonth,
  );
  return {
    months: completeMonths(new Set(
      normalized.months
        .filter((period) => period.status === "received")
        .map((period) => period.value),
    )),
    years: completeYears(new Set(
      normalized.years
        .filter((period) => period.status === "received")
        .map((period) => period.value),
    )),
  };
}

export function categorizeSources(
  sources: BuilderLegalStructureSourceDocument[],
  financialYearEndMonth: number,
): SourceCategory[] {
  const grouped = new Map<string, SourceCategory>();

  const addSource = (
    source: BuilderLegalStructureSourceDocument,
    kind: "table" | "document",
    type: string,
  ) => {
    const id = `${kind}:${type}`;
    const labels = kind === "table" ? TABLE_TYPE_LABELS : DOCUMENT_TYPE_LABELS;
    const category = grouped.get(id) ?? {
      id,
      label: labels[type] ?? readableSourceType(type),
      kind,
      type,
      cadence: sourceTypeCadence(kind, type),
      sources: [],
      months: [],
      years: [],
    };
    category.sources.push(source);
    grouped.set(id, category);
  };

  for (const source of sources) {
    const tableTypes = new Set(source.table_types);
    const documentTypes = new Set(source.document_types);
    tableTypes.forEach((type) => addSource(source, "table", type));
    documentTypes.forEach((type) => addSource(source, "document", type));
    if (tableTypes.size === 0 && documentTypes.size === 0) {
      addSource(source, "document", "unclassified");
    }
  }
  return Array.from(grouped.values())
    .map((category) => ({
      ...category,
      ...categoryPeriods(category, financialYearEndMonth),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
}
